import { ScheduleChips } from "@/components/BudgetSchedule";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Send,
  User as UserIcon,
  Download,
  Share2,
  Trophy,
  Eye,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { api, BRL } from "@/lib/api";
import BudgetSheet from "@/components/BudgetSheet";
import TermsOfBudgetDialog from "@/components/TermsOfBudgetDialog";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import FdpEntriesSchoolView from "@/components/FdpEntriesSchoolView";
import BudgetChatPanel from "@/components/BudgetChatPanel";
import RequestReopenButton, { canRequestReopen } from "@/components/RequestReopenButton";
import useAutoRefresh from "@/lib/useAutoRefresh";
import { Button } from "@/components/ui/button";

export default function SchoolBudgetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [budget, setBudget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [historyDialog, setHistoryDialog] = useState({ open: false, itemId: null });

  // Auto-open mandatory terms acceptance when school hasn't accepted yet
  // Competitors do not have terms — never auto-open on their view.
  useEffect(() => {
    if (budget && !budget.parent_budget_id && !budget.terms_accepted) {
      setTermsOpen(true);
    }
  }, [budget?.id, budget?.terms_accepted, budget?.parent_budget_id]);

  const acceptTerms = async () => {
    try {
      await api.post(`/school/budgets/${id}/accept-terms`);
      toast.success("Termo aceito");
      await load();
      setTermsOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao aceitar termo");
    }
  };

  const load = async () => {
    try {
      const b = await api.get(`/school/budgets/${id}`);
      setBudget(b.data);
    } catch (e) {
      toast.error("Orçamento não encontrado");
      nav("/escola/orcamentos");
    }
  };

  useEffect(() => { load(); window.scrollTo({ top: 0, behavior: "smooth" }); }, [id]);

  // Live refresh every 15s while tab is visible (do not reopen terms dialog on silent reload)
  const silentReload = async () => {
    try {
      const b = await api.get(`/school/budgets/${id}`);
      setBudget(b.data);
    } catch { /* ignore */ }
  };
  useAutoRefresh(silentReload, 15000, [id]);

  const decide = async (decision) => {
    const ok = window.confirm(`Confirmar ${decision === "aprovado" ? "aprovação" : "reprovação"} deste orçamento?`);
    if (!ok) return;
    setSaving(true);
    try {
      await api.patch(`/school/budgets/${id}/decision`, { decision });
      toast.success(decision === "aprovado" ? "Orçamento aprovado" : "Orçamento reprovado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/budgets/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orcamento_${budget.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao gerar PDF");
    }
  };

  const sharePdf = async () => {
    try {
      const r = await api.get(`/budgets/${id}/pdf`, { responseType: "blob" });
      const file = new File([r.data], `orcamento_${budget.number}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Orçamento ${budget.number}` });
      } else {
        downloadPdf();
      }
    } catch { toast.error("Falha ao compartilhar"); }
  };

  const downloadDeclaration = async () => {
    try {
      const r = await api.get(`/budgets/${id}/declaration-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `declaracao_simples_${budget.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar declaração");
    }
  };

  const downloadBundle = async () => {
    try {
      const r = await api.get(`/budgets/${id}/bundle`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pacote_${budget.number}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Pacote baixado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao baixar pacote");
    }
  };

  if (!budget) return <div className="text-sm text-slate-500">Carregando...</div>;

  const isCompetitor = !!budget.parent_budget_id;
  const canDecide = !isCompetitor && budget.status === "aguardando";
  const canReopen = !isCompetitor && canRequestReopen(budget.status);
  const concList = Array.isArray(budget.concorrencias) ? budget.concorrencias : [];
  const concCount = Number(budget.concorrencias_count || concList.length || 0);
  // Only show bundle download when EVERYTHING is ready: NF saved + NF file attached + at least 2 competitors
  const bundleReady = !isCompetitor && !!budget.nf_number && !!budget.nf_file_path && concCount >= 2;
  const hasDeclaration = !isCompetitor && !!budget.nf_number;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => nav(isCompetitor && budget.parent_budget_id ? `/escola/orcamentos/${budget.parent_budget_id}` : "/escola/orcamentos")} data-testid="s-back">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl md:text-2xl font-black text-[#1e3a8a]">
            {isCompetitor ? `Concorrência: ${budget.competitor_name || "—"}` : `Orçamento Nº ${budget.number}`}
          </h1>
          <p className="text-xs text-slate-500">
            {isCompetitor ? "Proposta recebida por terceiros vinculada ao seu orçamento" : "Visualização do orçamento"}
          </p>
          {!isCompetitor && <div className="mt-1"><ScheduleChips b={budget} showPayment={false} /></div>}
        </div>
        <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="s-download-pdf">
          <Download className="w-4 h-4 mr-1.5" />
          PDF
        </Button>
        {!isCompetitor && concList.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("s-concorrencias-box");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            data-testid="s-conc-alert"
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border-2 border-amber-300 bg-amber-50 text-amber-800 text-xs font-bold hover:bg-amber-100 transition-colors animate-pulse"
          >
            <Trophy className="w-4 h-4" />
            Possui {concList.length} concorrência{concList.length > 1 ? "s" : ""} ↓
          </button>
        )}
        <Button variant="outline" size="sm" onClick={sharePdf} data-testid="s-share-pdf" className="hidden sm:inline-flex">
          <Share2 className="w-4 h-4 mr-1.5" />
          Compartilhar
        </Button>
        {hasDeclaration && (
          <Button variant="outline" size="sm" onClick={downloadDeclaration} data-testid="s-download-declaration" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <Download className="w-4 h-4 mr-1.5" />
            Carta Simples
          </Button>
        )}
        {bundleReady && (
          <Button size="sm" onClick={downloadBundle} data-testid="s-download-bundle" className="bg-slate-900 hover:bg-slate-800 text-white">
            <Download className="w-4 h-4 mr-1.5" />
            Pacote (ZIP)
          </Button>
        )}
      </div>

      {!isCompetitor && budget.nfa_open && (() => {
        const items = budget.items || [];
        const done = items.filter((it) => it.completed).length;
        return (
          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4" data-testid="s-nfa-progress">
            <h3 className="font-heading text-sm font-bold text-indigo-900">Documentação emitida — serviços em andamento</h3>
            <p className="text-xs text-indigo-800 mt-0.5">
              <strong>{done} de {items.length}</strong> itens concluídos. Acompanhe abaixo cada item conforme a execução.
            </p>
            <div className="mt-2 h-2 rounded-full bg-white overflow-hidden">
              <div className="h-full bg-indigo-500 transition-[width] duration-500" style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
            </div>
          </div>
        );
      })()}

      {/* Reopen / reavaliação (only for non-closed statuses) */}
      {canReopen && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="s-reopen-box">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-sm font-bold text-orange-900">
              Precisa de ajustes neste orçamento?
            </h3>
            <p className="text-xs text-orange-800 mt-0.5">
              Peça uma reavaliação para a AMC — descreva o motivo e aguarde a revisão.
            </p>
          </div>
          <div className="w-full sm:w-auto sm:min-w-[200px]">
            <RequestReopenButton budget={budget} onDone={load} variant="detail" testid="s-request-reopen" />
          </div>
        </div>
      )}

      {/* Decision bar (if waiting) */}
      {canDecide && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-sm font-bold text-amber-900">
              Este orçamento aguarda sua aprovação
            </h3>
            <p className="text-xs text-amber-800">
              Analise os itens e informe se aprova ou reprova este orçamento.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => decide("aprovado")}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="s-approve"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Aprovar
            </Button>
            <Button
              onClick={() => decide("reprovado")}
              disabled={saving}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="s-reject"
            >
              <XCircle className="w-4 h-4 mr-1.5" />
              Reprovar
            </Button>
          </div>
        </div>
      )}

      {/* The sheet (BudgetSheet switches to competitor layout internally when parent_budget_id is set) */}
      <BudgetSheet
        budget={budget}
        onTermClick={() => setTermsOpen(true)}
        onItemHistoryClick={(iid) => setHistoryDialog({ open: true, itemId: iid })}
      />

      {/* General photos — visíveis para a escola assim que a AMC publicar */}
      {budget?.general_photos?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 md:p-5">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon className="w-4 h-4 text-[#7c3aed]" />
            <h3 className="font-heading text-base font-bold text-[#1e3a8a]">
              Fotos do serviço ({budget.general_photos.length})
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {budget.general_photos.map((p, idx) => {
              const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
              return (
                <a
                  key={p + idx}
                  href={src}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-violet-300 transition-all"
                  data-testid={`school-photo-${idx}`}
                >
                  <img src={src} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Fotos por item (antes/depois) — exibidas automaticamente pelo BudgetSheet;
          aqui adicionamos uma visão agregada com galeria por item quando houver */}
      {(() => {
        const perItem = (budget?.items || []).filter((it) => it.before_photo_path || it.after_photo_path);
        if (perItem.length === 0) return null;
        return (
          <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 md:p-5">
            <div className="flex items-center gap-2 mb-3">
              <ImageIcon className="w-4 h-4 text-emerald-600" />
              <h3 className="font-heading text-base font-bold text-[#1e3a8a]">
                Antes e depois ({perItem.length} item(ns))
              </h3>
            </div>
            <ul className="space-y-4">
              {perItem.map((it, i) => (
                <li key={it.id} className="border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
                  <p className="text-xs font-semibold text-slate-700 mb-2 line-clamp-2">{i + 1}. {it.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {it.before_photo_path && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.before_photo_path}`} target="_blank" rel="noreferrer" className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.before_photo_path}`} alt="Antes" className="w-full h-full object-cover" />
                        <span className="absolute top-1 left-1 text-[9px] font-black uppercase tracking-wider bg-orange-500 text-white px-1.5 py-0.5 rounded">Antes</span>
                      </a>
                    )}
                    {it.after_photo_path && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.after_photo_path}`} target="_blank" rel="noreferrer" className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.after_photo_path}`} alt="Depois" className="w-full h-full object-cover" />
                        <span className="absolute top-1 left-1 text-[9px] font-black uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded">Depois</span>
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {/* Concorrências — only on AMC parent view, outside the sheet */}
      {!isCompetitor && concList.length > 0 && (
        <ConcorrenciasBox amcBudget={budget} items={concList} />
      )}
      {/* FDP entries timeline — only for new-format FDP budgets */}
      <FdpEntriesSchoolView budget={budget} schoolName={budget?.school?.name || ""} />

      {/* Chat com a AMC */}
      {!isCompetitor && (
        <BudgetChatPanel budgetId={id} role="school" />
      )}

      {/* Botão para ver detalhamento FDP (ativo durante e após consolidação) */}
      {budget?.faz_depois_paga && (budget?.fdp_log_mode || budget?.fdp_consolidated_at) && (
        <a
          href={`/escola/orcamentos/${budget.id}/lancamentos`}
          className="block bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-4 text-white hover:shadow-lg transition-shadow"
          data-testid="btn-school-view-fdp-details"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">Prestação de contas FDP</p>
              <p className="text-sm font-black">Ver cada serviço com foto antes/depois</p>
              <p className="text-xs text-white/80 mt-0.5">Baixe o PDF da prestação pra sua pasta</p>
            </div>
            <span className="text-xl shrink-0">→</span>
          </div>
        </a>
      )}

      <TermsOfBudgetDialog
        open={termsOpen}
        onOpenChange={setTermsOpen}
        text={budget.awareness_term}
        mustAccept
        onAccept={acceptTerms}
        accepted={!!budget.terms_accepted}
        acceptedAt={budget.terms_accepted_at}
        acceptedBy={budget.terms_accepted_by}
        dismissable={!!budget.terms_accepted}
      />

      <ItemHistoryDialog
        open={historyDialog.open}
        onOpenChange={(o) => setHistoryDialog((d) => ({ ...d, open: o }))}
        budgetId={id}
        itemId={historyDialog.itemId}
      />
    </div>
  );
}

/**
 * School-side listing of linked competitor budgets.
 * Shown OUTSIDE the main orçamento sheet so the school can clearly see
 * this proposal has 2 competing proposals attached.
 */
function ConcorrenciasBox({ amcBudget, items }) {
  const nav = useNavigate();

  const downloadCompetitorPdf = async (c) => {
    try {
      const r = await api.get(`/budgets/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `concorrencia_${(c.competitor_name || "empresa").replace(/\s+/g, "_")}_${c.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao baixar o PDF");
    }
  };

  // Build comparison rows: AMC first, then competitors (sorted by total ASC so the cheapest is highlighted)
  const rows = [
    { name: "AMC Escolas", total: Number(amcBudget?.total || 0), accent: true, id: amcBudget?.id },
    ...items.map((c) => ({ name: c.competitor_name || "Concorrente", total: Number(c.total || 0), accent: false, id: c.id })),
  ];
  const cheapest = Math.min(...rows.map((r) => r.total || Infinity));

  return (
    <div
      id="s-concorrencias-box"
      className="bg-white rounded-2xl border-2 border-amber-200 card-shadow p-4 md:p-5 scroll-mt-24"
      data-testid="s-concorrencias-box"
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
          <Trophy className="w-4 h-4 text-amber-700" />
        </div>
        <h3 className="font-heading text-base md:text-lg font-black text-[#1e3a8a]">
          Este orçamento possui concorrências
        </h3>
      </div>
      <p className="text-xs text-slate-600 mb-4 pl-10">
        Para sua tranquilidade, enviamos também as propostas de outras {items.length} empresas para o mesmo serviço. Compare valores e condições.
      </p>

      {/* Comparativo rápido */}
      <div
        className="rounded-xl border border-slate-200 overflow-hidden mb-4"
        data-testid="s-conc-comparison"
      >
        <div className="bg-slate-50 px-4 py-2 flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
            Comparativo rápido
          </p>
          <span className="text-[10px] font-semibold text-emerald-700">
            Menor valor em destaque
          </span>
        </div>
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => {
            const isBest = r.total === cheapest && r.total > 0;
            return (
              <li
                key={r.id}
                className={`flex items-center justify-between px-4 py-2.5 ${isBest ? "bg-emerald-50" : "bg-white"}`}
                data-testid={`s-conc-cmp-${(r.name || "").toLowerCase().split(" ")[0]}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isBest && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-600 text-white text-[10px]">✓</span>}
                  <span className={`text-sm truncate ${r.accent ? "font-black text-[#1e3a8a]" : "font-semibold text-slate-700"}`}>
                    {r.name}
                    {r.accent && <span className="ml-1.5 text-[10px] font-bold text-[#7c3aed] uppercase">(seu orçamento)</span>}
                  </span>
                </div>
                <span className={`text-sm font-bold tabular-nums ${isBest ? "text-emerald-700" : "text-slate-800"}`}>
                  {BRL(r.total || 0)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {items.map((c) => (
          <div
            key={c.id}
            className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3"
            data-testid={`s-conc-card-${(c.competitor_name || "").toLowerCase().split(" ")[0]}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-sm font-black text-slate-900 truncate">
                  {c.competitor_name || "Concorrente"}
                </p>
                <p className="text-[11px] text-slate-500">
                  Proposta Nº {c.number}
                </p>
                <p className="text-sm font-bold text-emerald-700 mt-1">
                  {BRL(c.total || 0)}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  nav(`/escola/orcamentos/${c.id}`);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="flex-1"
                data-testid={`s-conc-view-${(c.competitor_name || "").toLowerCase().split(" ")[0]}`}
              >
                <Eye className="w-4 h-4 mr-1.5" />
                Visualizar
              </Button>
              <Button
                size="sm"
                onClick={() => downloadCompetitorPdf(c)}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid={`s-conc-pdf-${(c.competitor_name || "").toLowerCase().split(" ")[0]}`}
              >
                <Download className="w-4 h-4 mr-1.5" />
                PDF
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
